import React, { FC } from 'react';
import styles from '../journey-explorer.module.scss';
import { ChurnCohort } from '../../../../models/journey-explorer/journey-explorer';
import { COLORS, joinWords } from '../journey-explorer-utils';

const CHART_WIDTH = 284;
const CHART_HEIGHT = 96;
const BAR_WIDTH = 17;
const BAR_STEP = 23.5;
const BASELINE = 86;
const MAX_BAR = 76;
const PENDING_HEIGHT = 30;

interface ChurnCohortChartProps {
	cohorts: ChurnCohort[];
	followUpDays: number;
}

const monthDate = (month: string): Date => {
	const [year, index] = month.split('-').map(Number);
	return new Date(year, index - 1, 1);
};

const monthName = (month: string): string => monthDate(month).toLocaleString('en-GB', { month: 'long' });

const ChurnCohortChart: FC<ChurnCohortChartProps> = ({ cohorts, followUpDays }) => {
	const mature = cohorts.filter((cohort) => cohort.churn_pct !== null);
	const pending = cohorts.filter((cohort) => !cohort.mature).map((cohort) => monthName(cohort.month));
	const maxRate = Math.max(1, ...mature.map((cohort) => cohort.churn_pct ?? 0));
	const first = mature[0];
	const last = mature[mature.length - 1];

	const trend = () => {
		if (!first || !last || first === last || first.churn_pct === null || last.churn_pct === null) {
			return 'Not enough months have had time to show a trend yet.';
		}
		const from = `${first.churn_pct.toFixed(1)}%`;
		const to = `${last.churn_pct.toFixed(1)}%`;
		if (last.churn_pct === first.churn_pct) {
			return (
				<>
					Steady at <b>{from}</b> from {monthName(first.month)} to {monthName(last.month)} starters.
				</>
			);
		}
		const rising = last.churn_pct > first.churn_pct;
		return (
			<>
				{rising ? 'Up' : 'Down'} from <b>{from}</b> for {monthName(first.month)} starters to{' '}
				<b className={rising ? styles.churn_text : undefined}>{to}</b> for {monthName(last.month)}.
			</>
		);
	};

	return (
		<>
			<svg width={CHART_WIDTH} height={CHART_HEIGHT} className={styles.cohort_chart}>
				{cohorts.map((cohort, index) => {
					const x = index * BAR_STEP + 2;
					const initial = monthName(cohort.month).charAt(0);
					const rate = cohort.churn_pct;
					const height = rate === null ? 0 : (rate / maxRate) * MAX_BAR;
					return (
						<g key={cohort.month}>
							<title>
								{rate === null
									? `${monthName(cohort.month)} starters have not had ${followUpDays} days yet`
									: `${monthName(cohort.month)}: ${rate.toFixed(1)}% of ${cohort.customers} customers`}
							</title>
							{rate === null ? (
								<rect
									x={x}
									y={BASELINE - PENDING_HEIGHT}
									width={BAR_WIDTH}
									height={PENDING_HEIGHT}
									rx={3}
									fill="none"
									stroke={COLORS.line}
									strokeDasharray="2 2"
								/>
							) : (
								<rect
									x={x}
									y={BASELINE - height}
									width={BAR_WIDTH}
									height={height}
									rx={3}
									fill={COLORS.churn}
									opacity={0.55 + index * 0.03}
								/>
							)}
							<text x={x + BAR_WIDTH / 2} y={CHART_HEIGHT - 1} fontSize={9} textAnchor="middle" fill={COLORS.dim}>
								{initial}
							</text>
						</g>
					);
				})}
			</svg>
			<div className={styles.insights_trend}>
				{trend()}
				{pending.length > 0 && ` Dashed bars: ${joinWords(pending)} starters have not had ${followUpDays} days yet.`}
			</div>
		</>
	);
};

export default ChurnCohortChart;
